/**
 * Finds where a pointer was dragged over, e.g. an edge (before/after) or inside the element.
 *
 * @param {object} args
 * @param {number} args.pointer Current pointer position
 * @param {DOMRect} args.rect Bounding client rect of the target element
 * @param {'x' | 'y'} args.axis Which axis to evaluate ('x' for horizontal, 'y' for vertical)
 * @param {number} [args.thresholdRatio=0.25] Edge threshold as ratio of size
 * @param {number} [args.minThreshold=20] Minimum edge threshold in pixels
 * @returns {'before' | 'after' | 'inside'}
 */
export const getDropZone = ({
    pointer,
    rect,
    axis,
    thresholdRatio = 0.25,
    minThreshold = 20,
}) => {
    const start = axis === 'x' ? rect.left : rect.top;
    const size = axis === 'x' ? rect.width : rect.height;
    const edgeThreshold = Math.max(size * thresholdRatio, minThreshold);

    if (pointer < start + edgeThreshold) {
        return 'before';
    }

    if (pointer > start + size - edgeThreshold) {
        return 'after';
    }

    return 'inside';
};

/**
 * Finds at which position among a container's children a drop lands, from
 * where the pointer is: before the first child whose middle lies past the
 * pointer, or at the end when the pointer is past every child.
 *
 * Needed because a drop inside a container always went to the end, also in
 * the gap between two blocks, and the space below the bottom container gave
 * no clue where the block would land (updateverzoek Team Sportclub, 2026-10-14).
 *
 * @param {object} args
 * @param {number} args.pointer Current pointer position
 * @param {Array<{top: number, bottom: number, left: number, right: number}>} args.childRects Rects of the rendered children, in render order
 * @param {'x' | 'y'} args.axis Which axis the container lays its children out on
 * @returns {number} Index the dropped widget gets among the children
 */
export const getInsertIndex = ({ pointer, childRects, axis }) => {
    const index = childRects.findIndex((rect) => {
        const middle = axis === 'x'
            ? (rect.left + rect.right) / 2
            : (rect.top + rect.bottom) / 2;
        return pointer < middle;
    });

    return index === -1 ? childRects.length : index;
};
